import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Regular client for auth and normal operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
  },
});

// Anon-only client for public pages (no auth headers attached).
// CRITICAL: a distinct storageKey. Without it this second GoTrueClient shares the
// default `sb-<ref>-auth-token` key with `supabase` above → "Multiple GoTrueClient
// instances detected … undefined behavior when used concurrently under the same
// storage key" (two clients contending on the same auth-token lock: session churn,
// spurious onAuthStateChange events, the staff resume-loop). A separate key +
// no-op storage keeps this client fully isolated from the real auth session.
export const supabaseAnon = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'sb-looma-anon-public',
    storage: {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    },
  },
});

// Abort a Supabase request after `ms` so a stalled socket (iOS / 4G) can't hang
// the UI. Manual AbortController for iOS < 16 compat (AbortSignal.timeout is 16+).
// Pair with React Query `retry` so the aborted request retries on a fresh socket.
export function timeoutSignal(ms: number): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

// True on the staff POS path (code-based auth; consumes neither Auth nor
// Organization owner-context). Used to skip owner-session fetches there so the
// pilot terminal never blocks on data it doesn't use.
export function isStaffPosPath(pathname: string): boolean {
  return pathname.includes('/pos-staff');
}

// Admin API helper — calls SECURITY DEFINER RPCs with the user's JWT
// The RPCs verify super_admin role internally; service role key stays server-side.
export async function adminApi<T = unknown>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  // Convert action: 'list-users' → 'admin_list_users'
  const rpcName = `admin_${action.replace(/-/g, '_')}`;

  // Convert camelCase keys to snake_case with p_ prefix for SQL params
  const rpcParams: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    const snakeKey = 'p_' + key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    rpcParams[snakeKey] = value;
  }

  const { data, error } = await supabase.rpc(rpcName, rpcParams);

  if (error) {
    throw new Error(error.message || 'Admin API call failed');
  }

  return data as T;
}
